import type { QueryClient } from '@tanstack/react-query';

import { meAccessQueryOptions, sidebarQueryOptions } from '../../../access';
import i18n from '../../../i18n';
import { languageChoiceOf, languageInUse } from '../../../i18n/languages';
import { DETAILS_UNREAD, SIGNED_OUT, currentUserIdOf, forgetCachedData, openSessionHere, replaceSession, sessionQueryOptions } from '../../../session';
import type { SignedInSession } from '../../../session';
import { resetDashboardSession } from '../../../store/dashboardSession';
import { analytics } from '../../analytics';
import { LOGIN_SESSION_START } from '../../analytics/events';
import { trackEvent } from '../../analytics/trackingMiddleware';
import type { OpenedSession, SessionLayout } from '../types/domain.types';

export type LoginMethod = 'passcode' | 'password';

/**
 * The sidebar sign-in sent, in this tab's memory only, filed under the language the
 * person is about to see. Sign-in does not say which language its labels are in, so
 * it is shown at once and read again in that language straight away.
 */
const seedSidebar = (queryClient: QueryClient, session: OpenedSession, layout: SessionLayout): void => {
  const language = session.language === null ? languageInUse(i18n) : languageChoiceOf(session.language);
  queryClient.setQueryData(sidebarQueryOptions(language).queryKey, layout.menu ?? layout.sideBar, { updatedAt: 0 });
};

const knownLandingPageOf = (queryClient: QueryClient, userId: string): string | null => {
  const known = queryClient.getQueryData(sessionQueryOptions().queryKey);
  return known?.status === 'signedIn' && known.user.id === userId ? known.landingPage : null;
};

const signedInSessionOf = (session: OpenedSession, landingPage: string | null): SignedInSession => ({
  status: 'signedIn',
  user: {
    id: session.userId,
    name: session.userName,
    email: session.email,
    isStaff: session.isStaff,
  },
  details: DETAILS_UNREAD,
  viewingAs: null,
  landingPage,
});

/** Every way in — code, password, an issue link — opens the same session. */
export const startSession = (queryClient: QueryClient, session: OpenedSession, method: LoginMethod = 'passcode'): void => {
  const previousUserId = currentUserIdOf(queryClient);
  const landingPage = session.layout === null ? knownLandingPageOf(queryClient, session.userId) : session.layout.landingPage;
  // Someone else signing in over another person's session ends that one here first, so nothing held for them carries over.
  if (previousUserId !== null && previousUserId !== session.userId) replaceSession(queryClient, SIGNED_OUT);
  forgetCachedData(queryClient);
  resetDashboardSession();
  if (session.access !== null) queryClient.setQueryData(meAccessQueryOptions().queryKey, session.access);
  if (session.layout !== null) seedSidebar(queryClient, session, session.layout);
  void analytics.identifyUser({ distinctId: session.userId, userProperties: { name: session.userName } });
  trackEvent(LOGIN_SESSION_START, { method });
  openSessionHere(queryClient, signedInSessionOf(session, landingPage));
};
